import { useState, useEffect, useCallback } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { acquisitionsApi } from "@/lib/api/acquisitions";
import { Loader2, BarChart3, Store, ScrollText, Lightbulb } from "lucide-react";

type FundRow = Awaited<ReturnType<typeof acquisitionsApi.reports.fundUtilization>>["funds"][number];
type VendorRow = Awaited<ReturnType<typeof acquisitionsApi.reports.vendorSpend>>["vendors"][number];
type AuditRow = Awaited<ReturnType<typeof acquisitionsApi.reports.auditLog>>[number];

export default function AcquisitionsReportsPage() {
  const [tab, setTab] = useState("funds");
  const [funds, setFunds] = useState<FundRow[]>([]);
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [audit, setAudit] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [fu, vs, al] = await Promise.all([
        acquisitionsApi.reports.fundUtilization(),
        acquisitionsApi.reports.vendorSpend(),
        acquisitionsApi.reports.auditLog({ limit: 150 }),
      ]);
      setFunds(fu.funds);
      setVendors(vs.vendors);
      setAudit(al);
    } catch (e) {
      console.error(e);
      setFunds([]);
      setVendors([]);
      setAudit([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fundCols: DataTableColumn<FundRow>[] = [
    { id: "name", header: "Fund", cell: (r) => <span className="font-medium">{r.name}</span> },
    {
      id: "code",
      header: "Code",
      cell: (r) => <span className="font-mono text-sm">{r.code}</span>,
    },
    {
      id: "fy",
      header: "FY",
      cell: (r) => <span className="tabular-nums">{r.fiscalYear}</span>,
      width: "w-16",
    },
    {
      id: "alloc",
      header: "Allocation",
      cell: (r) => <span className="tabular-nums">₦{r.allocation.toLocaleString()}</span>,
    },
    {
      id: "used",
      header: "Used (enc+exp)",
      cell: (r) => (
        <span className="tabular-nums text-slate-700">
          ₦{(r.encumbered + r.expended).toLocaleString()}
        </span>
      ),
    },
    {
      id: "util",
      header: "Util. %",
      cell: (r) => <span className="tabular-nums font-medium">{r.utilizationPercent}%</span>,
      width: "w-20",
    },
  ];

  const vendorCols: DataTableColumn<VendorRow>[] = [
    { id: "name", header: "Vendor", cell: (r) => <span className="font-medium">{r.name}</span> },
    {
      id: "code",
      header: "Code",
      cell: (r) => <span className="font-mono text-sm">{r.code}</span>,
    },
    {
      id: "lines",
      header: "PO lines",
      cell: (r) => <span className="tabular-nums">{r.lineCount}</span>,
      width: "w-24",
    },
    {
      id: "spend",
      header: "Line total (est.)",
      cell: (r) => (
        <span className="tabular-nums">₦{Math.round(r.totalSpend).toLocaleString()}</span>
      ),
    },
  ];

  const auditCols: DataTableColumn<AuditRow>[] = [
    {
      id: "when",
      header: "When",
      cell: (r) => (
        <span className="text-xs text-slate-600 whitespace-nowrap">
          {new Date(r.createdAt).toLocaleString()}
        </span>
      ),
      width: "w-40",
    },
    {
      id: "action",
      header: "Action",
      cell: (r) => <span className="text-xs font-mono">{r.action}</span>,
      width: "w-24",
    },
    {
      id: "resource",
      header: "Resource",
      cell: (r) => (
        <span className="text-xs">
          {r.resourceType}
          {r.resourceId ? (
            <span className="text-slate-400 ml-1 font-mono truncate max-w-[120px] inline-block align-bottom">
              {r.resourceId.slice(0, 8)}…
            </span>
          ) : null}
        </span>
      ),
    },
    {
      id: "desc",
      header: "Description",
      cell: (r) => <span className="text-sm text-slate-700">{r.description ?? "—"}</span>,
    },
    {
      id: "user",
      header: "User",
      cell: (r) => <span className="text-xs text-slate-500">{r.user?.email ?? "—"}</span>,
      width: "w-40",
    },
  ];

  return (
    <PageContainer width="wide">
      <PageHeader
        title="Acquisitions insights"
        description="Fund utilization, vendor spend from PO lines, and module audit activity."
        breadcrumbItems={[
          { label: "Dashboard", href: "/dashboard" },
          { label: "Acquisitions insights" },
        ]}
        showSearch={false}
        titleSize="md"
      />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="funds" className="gap-1.5">
            <BarChart3 className="h-4 w-4" />
            Fund utilization
          </TabsTrigger>
          <TabsTrigger value="vendors" className="gap-1.5">
            <Store className="h-4 w-4" />
            Vendor spend
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-1.5">
            <ScrollText className="h-4 w-4" />
            Audit log
          </TabsTrigger>
          <TabsTrigger value="roadmap" className="gap-1.5">
            <Lightbulb className="h-4 w-4" />
            Innovation
          </TabsTrigger>
        </TabsList>

        <TabsContent value="funds" className="space-y-3">
          <p className="text-sm text-slate-600">
            Active funds: utilization is (encumbered + expended) ÷ allocation.
          </p>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <DataTable columns={fundCols} data={funds} rowKey={(r) => r.id} loading={false} />
          )}
        </TabsContent>

        <TabsContent value="vendors" className="space-y-3">
          <p className="text-sm text-slate-600">
            Sum of PO line amounts (unit price × quantity) for orders that are not cancelled.
          </p>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <DataTable
              columns={vendorCols}
              data={vendors}
              rowKey={(r) => r.vendorId}
              loading={false}
            />
          )}
        </TabsContent>

        <TabsContent value="audit" className="space-y-3">
          <p className="text-sm text-slate-600">
            Recent CREATE/UPDATE events logged for acquisition requests, purchase orders, and
            related entities.
          </p>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <DataTable columns={auditCols} data={audit} rowKey={(r) => r.id} loading={false} />
          )}
        </TabsContent>

        <TabsContent value="roadmap" className="rounded-lg border bg-slate-50/80 p-6 space-y-3">
          <h3 className="font-semibold text-slate-900">Innovation &amp; backlog ideas</h3>
          <ul className="list-disc pl-5 text-sm text-slate-700 space-y-2">
            <li>Batch purchase orders and a unified wizard across request → PO → receive.</li>
            <li>Richer timeline with deep links to catalog records and invoices.</li>
            <li>Saved views and column presets on request and PO lists.</li>
            <li>EDI / vendor portal integrations (long-term).</li>
          </ul>
          <p className="text-xs text-slate-500 pt-2">
            Core workflows are available from the Acquisitions sidebar; this page focuses on
            analytics and transparency.
          </p>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
